import { useEffect, useRef } from 'react';

/**
 * Görgetés-vezérelt CSS-változók React-újrarender NÉLKÜL (a framer-motion useScroll/useTransform
 * helyett a kritikus úton). A `compute` a scrollY / viewport / elem alapján CSS custom property-ket ad
 * vissza, ezeket rAF-throttle-lal az elemre írjuk; a stílus `var(--…)`-ral használja őket.
 * A `compute` legyen komponensen kívül definiált vagy useCallback-elt (stabil referencia).
 */
export type ScrollVars = Record<`--${string}`, string | number>;

export function useScrollDriven<T extends HTMLElement>(
  compute: (scrollY: number, viewportH: number, el: T) => ScrollVars,
) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const vars = compute(window.scrollY, window.innerHeight, el);
      for (const k in vars) el.style.setProperty(k, String(vars[k as `--${string}`]));
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [compute]);

  return ref;
}

export const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/** 0 → 1 az [a, b] tartományon (a framer useTransform két-pontos leképezése). */
export const ramp = (v: number, a: number, b: number) => clamp01((v - a) / (b - a));

/** Az oldal teljes görgetési aránya (0 = teteje, 1 = alja). */
export const pageProgress = (scrollY: number, viewportH: number) => {
  const max = document.documentElement.scrollHeight - viewportH;
  return max > 0 ? clamp01(scrollY / max) : 0;
};
